import { supabase } from "@/integrations/supabase/client";

export type CostPeriod = "all" | string; // "all" o "YYYY-MM"

export const NO_COMPANY_ID = "__sin_empresa__";

export interface CompanyCostRow {
  companyId: string;
  name: string;
  organizationId: string;
  organizationName: string;
  documents: number;
  aiCalls: number;
  aiCredits: number;
  aiEur: number; // real
  aiEurReal: number;
  desarrolloEur: number;
  hostingEur: number;
  runningEur: number;
  devEur: number;
  otherEur: number; // desarrollo + running
  totalEur: number;
  share: number; // 0..1 sobre el coste total
}

export interface CompanyCostReport {
  rows: CompanyCostRow[];
  totals: {
    documents: number;
    aiCalls: number;
    aiCredits: number;
    aiEur: number;
    aiEurReal: number;
    desarrolloEur: number;
    hostingEur: number;
    runningEur: number;
    devEur: number;
    otherEur: number;
    totalEur: number;
  };
}

/** Lee una tabla completa por páginas (PostgREST limita el número de filas por petición). */
const fetchAll = async <T,>(table: string, columns: string, filter?: (q: any) => any): Promise<T[]> => {
  const pageSize = 1000;
  const out: T[] = [];
  for (let from = 0; ; from += pageSize) {
    let query: any = supabase.from(table as any).select(columns);
    if (filter) query = filter(query);
    const { data, error } = await query.range(from, from + pageSize - 1);
    if (error) {
      console.error(`fetchAll ${table}:`, error);
      break;
    }
    const rows = (data ?? []) as T[];
    out.push(...rows);
    if (rows.length < pageSize) break;
  }
  return out;
};



const monthKeyOf = (iso: string) => iso.slice(0, 7);

export const currentMonthKey = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const lastMonthKeys = (n: number): string[] => {
  const keys: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
};

export const monthLabel = (mk: string): string => {
  const [y, m] = mk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("es-ES", {
    month: "long",
    year: "numeric",
  });
};

const inPeriod = (iso: string, period: CostPeriod) => period === "all" || monthKeyOf(iso) === period;

interface MonthActivity {
  documentsByCompany: Map<string, number>;
  usersByCompany: Map<string, Set<string>>;
}

export interface OrganizationOption {
  id: string;
  name: string;
  slug: string;
}

export const listOrganizations = async (): Promise<OrganizationOption[]> => {
  const { data, error } = await supabase
    .from("organizations")
    .select("id, name, slug")
    .order("name", { ascending: true })
    .limit(500);
  if (error || !data) {
    console.error("listOrganizations:", error);
    return [];
  }
  return data as OrganizationOption[];
};

/**
 * Documentos tramitados y coste (IA + desarrollo/hosting/running prorrateado) por empresa.
 */
export const getCompanyCostReport = async (
  period: CostPeriod,
  organizationId?: "all" | string,
): Promise<CompanyCostReport> => {
  const [events, ai, costs, orgsRes] = await Promise.all([
    fetchAll<{ company_id: string | null; user_id: string | null; count: number; occurred_at: string }>(
      "processing_events",
      "company_id, user_id, count, occurred_at, event_type",
      (q) => q.eq("event_type", "processed"),
    ),
    fetchAll<{ company_id: string | null; occurred_at: string; credits: number; eur: number; operation: string }>(
      "ai_usage_events",
      "company_id, occurred_at, credits, eur, operation",
    ),
    fetchAll<{ month: string; amount_eur: number; allocation_basis: string; cost_type: string }>(
      "development_costs",
      "month, amount_eur, allocation_basis, cost_type",
    ),
    supabase.from("organizations").select("id, name").limit(500),
  ]);

  const orgNames = new Map((orgsRes.data ?? []).map((o) => [o.id as string, o.name as string]));

  // Actividad por mes (necesaria para prorratear los costes)
  const activity = new Map<string, MonthActivity>();
  for (const ev of events) {

    const cid = (ev.company_id as string | null) ?? NO_COMPANY_ID;
    const mk = monthKeyOf(ev.occurred_at as string);
    const entry =
      activity.get(mk) ?? { documentsByCompany: new Map(), usersByCompany: new Map() };
    entry.documentsByCompany.set(cid, (entry.documentsByCompany.get(cid) ?? 0) + (Number(ev.count) || 0));
    if (ev.user_id) {
      const set = entry.usersByCompany.get(cid) ?? new Set<string>();
      set.add(ev.user_id as string);
      entry.usersByCompany.set(cid, set);
    }
    activity.set(mk, entry);
  }

  const rows = new Map<string, CompanyCostRow>();
  const ensure = (cid: string): CompanyCostRow => {
    const existing = rows.get(cid);
    if (existing) return existing;
    const row: CompanyCostRow = {
      companyId: cid,
      name: cid === NO_COMPANY_ID ? "Sin empresa asignada" : "Empresa desconocida",
      organizationId: "",
      organizationName: "",
      documents: 0,
      aiCalls: 0,
      aiCredits: 0,
      aiEur: 0,
      aiEurReal: 0,
      desarrolloEur: 0,
      hostingEur: 0,
      runningEur: 0,
      devEur: 0,
      otherEur: 0,
      totalEur: 0,
      share: 0,

    };
    rows.set(cid, row);
    return row;
  };

  // Documentos del periodo
  for (const [mk, act] of activity) {
    if (period !== "all" && mk !== period) continue;
    for (const [cid, n] of act.documentsByCompany) ensure(cid).documents += n;
  }

  // Consumo de IA del periodo (real)
  for (const ev of ai) {
    const cid = (ev.company_id as string | null) ?? NO_COMPANY_ID;
    if (!inPeriod(ev.occurred_at as string, period)) continue;
    const row = ensure(cid);
    row.aiCalls += 1;
    row.aiCredits += Number(ev.credits) || 0;
    row.aiEurReal += Number(ev.eur) || 0;
    row.aiEur = row.aiEurReal;
  }



  // Actividad acumulada de todos los meses (fallback para costes de meses sin actividad)
  const globalAct: MonthActivity = { documentsByCompany: new Map(), usersByCompany: new Map() };
  for (const act of activity.values()) {
    for (const [cid, n] of act.documentsByCompany) {
      globalAct.documentsByCompany.set(cid, (globalAct.documentsByCompany.get(cid) ?? 0) + n);
    }
    for (const [cid, set] of act.usersByCompany) {
      const gset = globalAct.usersByCompany.get(cid) ?? new Set<string>();
      for (const u of set) gset.add(u);
      globalAct.usersByCompany.set(cid, gset);
    }
  }

  // Costes prorrateados según la actividad del mes de cada coste.
  // Si el mes no tiene actividad, se reparte según la actividad acumulada; y si
  // tampoco la hay, el coste se mantiene en los totales como "sin repartir".
  let unassignedDevEur = 0;
  let unassignedDesarrolloEur = 0;
  let unassignedHostingEur = 0;
  let unassignedRunningEur = 0;
  for (const cost of costs) {
    const mk = monthKeyOf(cost.month as string);
    if (period !== "all" && mk !== period) continue;
    const amount = Number(cost.amount_eur) || 0;
    const act = activity.get(mk) ?? globalAct;
    const basis = (cost.allocation_basis as string) === "users" ? "users" : "documents";
    const costType = (cost.cost_type as string) === "hosting" || (cost.cost_type as string) === "running"
      ? (cost.cost_type as "hosting" | "running")
      : "desarrollo";
    const weights = new Map<string, number>();
    let totalWeight = 0;
    if (basis === "users") {
      for (const [cid, set] of act.usersByCompany) {
        weights.set(cid, set.size);
        totalWeight += set.size;
      }
    } else {
      for (const [cid, n] of act.documentsByCompany) {
        weights.set(cid, n);
        totalWeight += n;
      }
    }
    if (totalWeight <= 0) {
      unassignedDevEur += amount;
      if (costType === "hosting") unassignedHostingEur += amount;
      else if (costType === "running") unassignedRunningEur += amount;
      else unassignedDesarrolloEur += amount;
      continue;
    }
    for (const [cid, w] of weights) {
      const row = ensure(cid);
      const share = (amount * w) / totalWeight;
      if (costType === "hosting") row.hostingEur += share;
      else if (costType === "running") row.runningEur += share;
      else row.desarrolloEur += share;
    }
  }

  const list = [...rows.values()];


  // Nombres de empresa y asesoría
  const realIds = list.map((r) => r.companyId).filter((id) => id !== NO_COMPANY_ID);
  if (realIds.length) {
    const { data } = await supabase
      .from("company_masters")
      .select("id, name, organization_id")
      .in("id", realIds);
    for (const r of list) {
      if (r.companyId === NO_COMPANY_ID) continue;
      const found = (data ?? []).find((c) => c.id === r.companyId);
      r.name = found?.name ?? "Empresa desconocida";
      r.organizationId = (found?.organization_id as string) ?? "";
      r.organizationName = orgNames.get(r.organizationId) ?? "";
    }
  }

  // Filtrar por asesoría
  const filteredList =
    organizationId && organizationId !== "all"
      ? list.filter((r) => r.organizationId === organizationId)
      : list;

  for (const r of filteredList) {
    r.devEur = r.desarrolloEur + r.hostingEur + r.runningEur;
    r.otherEur = r.desarrolloEur + r.runningEur;
    r.totalEur = r.aiEur + r.devEur;
  }

  const totals = filteredList.reduce(
    (acc, r) => ({
      documents: acc.documents + r.documents,
      aiCalls: acc.aiCalls + r.aiCalls,
      aiCredits: acc.aiCredits + r.aiCredits,
      aiEur: acc.aiEur + r.aiEur,
      aiEurReal: acc.aiEurReal + r.aiEurReal,
      desarrolloEur: acc.desarrolloEur + r.desarrolloEur,
      hostingEur: acc.hostingEur + r.hostingEur,
      runningEur: acc.runningEur + r.runningEur,
      devEur: acc.devEur + r.devEur,
      otherEur: acc.otherEur + r.otherEur,
      totalEur: acc.totalEur + r.totalEur,
    }),
    {
      documents: 0,
      aiCalls: 0,
      aiCredits: 0,
      aiEur: 0,
      aiEurReal: 0,
      desarrolloEur: 0,
      hostingEur: 0,
      runningEur: 0,
      devEur: 0,
      otherEur: 0,
      totalEur: 0,
    },
  );

  // Los costes sin actividad alguna no se pierden: van a los totales.
  totals.devEur += unassignedDevEur;
  totals.desarrolloEur += unassignedDesarrolloEur;
  totals.hostingEur += unassignedHostingEur;
  totals.runningEur += unassignedRunningEur;
  totals.otherEur += unassignedDesarrolloEur + unassignedRunningEur;
  totals.totalEur += unassignedDevEur;

  for (const r of filteredList) r.share = totals.totalEur > 0 ? r.totalEur / totals.totalEur : 0;

  return {
    rows: filteredList
      .filter((r) => r.documents > 0 || r.aiCalls > 0 || r.totalEur > 0)
      .sort((a, b) => b.documents - a.documents || b.totalEur - a.totalEur),
    totals,

  };
};


export interface UserActivityRow {
  userId: string;
  name: string;
  email: string;
  documents: number;
}

/**
 * Documentos tramitados por usuario en un periodo.
 */
export const getUserActivityReport = async (
  period: CostPeriod,
): Promise<{ rows: UserActivityRow[]; total: number }> => {
  const { data, error } = await supabase
    .from("processing_events")
    .select("user_id, count, occurred_at, event_type")
    .eq("event_type", "processed")
    .limit(50000);

  if (error) {
    console.error("getUserActivityReport:", error);
    return { rows: [], total: 0 };
  }

  const byUser = new Map<string, number>();
  for (const ev of data ?? []) {
    if (!ev.user_id) continue;
    if (!inPeriod(ev.occurred_at as string, period)) continue;
    byUser.set(ev.user_id as string, (byUser.get(ev.user_id as string) ?? 0) + (Number(ev.count) || 0));
  }

  const userIds = [...byUser.keys()];
  const profiles = new Map<string, { full_name: string; email: string }>();
  if (userIds.length) {
    const { data: profs } = await supabase
      .from("profiles")
      .select("id, full_name, email")
      .in("id", userIds);
    for (const p of profs ?? []) profiles.set(p.id, { full_name: p.full_name, email: p.email });
  }

  const rows: UserActivityRow[] = userIds.map((uid) => ({
    userId: uid,
    name: profiles.get(uid)?.full_name || "Usuario desconocido",
    email: profiles.get(uid)?.email ?? "—",
    documents: byUser.get(uid) ?? 0,
  }));
  rows.sort((a, b) => b.documents - a.documents);

  return { rows, total: rows.reduce((acc, r) => acc + r.documents, 0) };
};
